import {
  Box,
  Chip,
  Container,
  LinearProgress,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { AccountButton } from "@ssf/auth-components";
import { PlayLogo } from "@ssf/components";
import { PairingSystem } from "@ssf/memdb-api";
import { ArenaRedirect } from "@ssf/play-components";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, matchPath, useLocation } from "react-router";
import {
  generatePath,
  routes,
  useManagedTournamentGroups,
  useMemdbTournament,
  usePlayer,
  useRoom,
} from "../lib/index.js";
import { AdminMenu, TournamentClock } from "./index.js";

export const Header = () => {
  const { t } = useTranslation("Header");
  const { player } = usePlayer();
  const { data: managedGroups = [] } = useManagedTournamentGroups();
  const managedArenaGroups = useMemo(
    () =>
      managedGroups.filter(
        ({ pairingsystem }) => pairingsystem === PairingSystem.Arena,
      ),
    [managedGroups],
  );

  const { pathname } = useLocation();
  const {
    params: { room: roomKey },
  } = matchPath(`${routes.lobby}/*`, pathname) || { params: {} };
  const { data: room, isFetched } = useRoom(roomKey);

  const { data: memdbGroup } = useMemdbTournament(
    room?.type === "tournament" ? room.memdbGroupId : 0,
    { enabled: room?.type === "tournament", retry: true },
  );

  return (
    <>
      <LinearProgress
        value={100}
        variant="determinate"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          "> span": { background: ({ palette }) => palette.primary.main },
        }}
      />
      <Container sx={{ maxWidth: "100% !important" }}>
        <Stack
          spacing={1}
          direction="row"
          sx={{
            alignItems: "center",
            minHeight: 48,
          }}
        >
          <MuiLink
            component={Link}
            to={
              room && roomKey
                ? generatePath("lobby", { room: roomKey })
                : generatePath("home", {})
            }
            sx={{
              mr: "auto",
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <PlayLogo style={{ width: "28px", marginRight: "10px" }} />
            <Typography
              component="h1"
              variant="h4"
              noWrap
              sx={(theme) => ({
                [theme.breakpoints.down("sm")]: { display: "none" },
              })}
            >
              {isFetched && (room?.name || t("title"))}
            </Typography>
          </MuiLink>

          <Box sx={{ flexGrow: 1 }} />

          {memdbGroup?.pairingsystem === PairingSystem.Arena &&
            "start" in memdbGroup && <TournamentClock {...memdbGroup} />}

          {player?.kind === "guest" && <Chip label={player.name} />}
          <AccountButton />
          {roomKey && <AdminMenu roomKey={roomKey} room={room} />}
        </Stack>
      </Container>

      {isFetched && (
        <ArenaRedirect
          additionalGroups={managedArenaGroups}
          currentGroupId={
            room?.type === "tournament" ? room.memdbGroupId : undefined
          }
          actionProps={(groupId) => ({
            component: Link,
            to: `${generatePath("memdbRedirect", {})}?groupId=${groupId}`,
          })}
        />
      )}
    </>
  );
};
